import { ImageWithFallback } from "@/app/components/figma/ImageWithFallback";
import { isStoragePath, normalizeAdImageStoragePath } from "@/app/utils/ad-utils";
import { useAdImageUrl } from "@/app/hooks/use-ad-image-url";
import { cn } from "@/app/components/ui/utils";

interface AdImageProps {
  /** URL (http/https/data) or storage path (ads/...) to resolve via signed URL */
  src: string | null | undefined;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
  /** Placeholder while signed URL is loading */
  placeholderClassName?: string;
}

/**
 * Renders an ad image. If src is a storage path (ads/...), fetches a signed URL and displays it.
 * Otherwise uses the URL as-is (with ImageWithFallback).
 */
export function AdImage({
  src,
  alt,
  className,
  loading = "lazy",
  placeholderClassName,
}: AdImageProps) {
  const normalized =
    typeof src === "string" ? normalizeAdImageStoragePath(src) : "";
  const isPath = normalized.length > 0 && isStoragePath(normalized);
  const { url, loading: resolving } = useAdImageUrl(isPath ? normalized : null);

  if (!src?.trim()) {
    return (
      <div
        className={cn("bg-muted flex items-center justify-center", className)}
        aria-label={alt}
      >
        <span className="text-muted-foreground text-xs">Sem imagem</span>
      </div>
    );
  }

  if (isPath) {
    if (resolving || !url) {
      return (
        <div
          className={cn(
            "bg-muted animate-pulse flex items-center justify-center",
            className,
            placeholderClassName
          )}
          aria-label={alt}
        />
      );
    }
    return (
      <ImageWithFallback
        src={url}
        alt={alt}
        className={className}
        loading={loading}
      />
    );
  }

  return (
    <ImageWithFallback
      src={normalized || src}
      alt={alt}
      className={className}
      loading={loading}
    />
  );
}
